import './App.css';
import React, {useRef, useEffect, useState} from 'react';
import * as tf from '@tensorflow/tfjs';
import * as facemesh from '@tensorflow-models/facemesh';
import Webcam from 'react-webcam';
import {drawMesh} from './utilities';

// Eye landmark indices for EAR calculation (left and right)
const LEFT_EYE_INDICES = [33, 160, 158, 133, 153, 144];
const RIGHT_EYE_INDICES = [263, 387, 385, 362, 380, 373];
// Calculate Euclidean distance (supports 2D or 3D points)
const euclidean = (a, b) => {
  if (a.length === 3 && b.length === 3) {
    return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  }
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
};

// Calculate Eye Aspect Ratio
const getEAR = (eye) => {
  const A = euclidean(eye[1], eye[5]);
  const B = euclidean(eye[2], eye[4]);
  const C = euclidean(eye[0], eye[3]);
  return (A + B) / (2.0 * C);
};

function App() {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [blinkCount, setBlinkCount] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);
  const lastBlinkTimeRef = useRef(0);
  const BLINK_DELAY = 400; // milliseconds

  // New: Track if eyes are currently closed
  const eyesClosedRef = useRef(false);

  useEffect(() => {
    const runFacemesh = async () => {
      const net = await facemesh.load({
        inputResolution: {width: 640, height: 480},
        scale: 0.8
      });
      setInterval(() => {
        detect(net)
      }, 100);
    };
    runFacemesh();
    // eslint-disable-next-line
  }, []);

  const detect = async (net) => {
    if (
      typeof webcamRef.current !== "undefined" && 
      webcamRef.current !== null && 
      webcamRef.current.video.readyState === 4) {
        const video = webcamRef.current.video;
        const videoWidth = webcamRef.current.video.videoWidth;
        const videoHeight = webcamRef.current.video.videoHeight;

        webcamRef.current.video.width = videoWidth;
        webcamRef.current.video.height = videoHeight;
        canvasRef.current.width = videoWidth;
        canvasRef.current.height = videoHeight;

        const faces = await net.estimateFaces(video);

        // Blink detection with delay and prevent auto-increment while eyes closed
        if (faces.length > 0) {
          const face = faces[0];
          const mesh = face.scaledMesh;
          
          const leftEye = LEFT_EYE_INDICES.map(idx => mesh[idx]);
          const rightEye = RIGHT_EYE_INDICES.map(idx => mesh[idx]);

          const leftEAR = getEAR(leftEye);
          const rightEAR = getEAR(rightEye);
          const avgEAR = (leftEAR + rightEAR) / 2;

          const BLINK_THRESHOLD = 0.22;
          const now = Date.now();

          if (avgEAR < BLINK_THRESHOLD) {
            // Eyes are closed
            if (!eyesClosedRef.current) {
              // Eyes just closed, count as a blink
              if (!isBlinking && now - lastBlinkTimeRef.current > BLINK_DELAY) {
                setIsBlinking(true);
                setBlinkCount(prev => prev + 1);
                lastBlinkTimeRef.current = now;
              }
              eyesClosedRef.current = true;
            }
            // If eyes are already closed, do nothing (prevents auto-increment)
          } else {
            // Eyes are open
            setIsBlinking(false);
            eyesClosedRef.current = false;
          }
        }

        const ctx = canvasRef.current.getContext("2d");
        drawMesh(faces, ctx);
      }
  };

  return (
    <div className="App">
      <div style={{position: 'absolute', top: 10, left: 10, color: 'red', fontSize: 24, zIndex: 10}}>
        Blinks: {blinkCount}
      </div>
      <Webcam ref={webcamRef} style={
        {
          position: 'absolute',
          marginLeft: 'auto',
          marginRight: 'auto',
          left: 0,
          right: 0,
          textAlign: 'center',
          zindex: 9,
          width: 640,
          height: 480
        }
      }/>
      <canvas ref={canvasRef} style={
        {
          position: 'absolute',
          marginLeft: 'auto',
          marginRight: 'auto',
          left: 0,
          right: 0,
          textAlign: 'center',
          zindex: 9,
          width: 640,
          height: 480
        }
      }/>
    </div>
  );
}

export default App;
